import type { TrackingSettings } from './hooks/useTrackingSettings';

type GtagFn = (...args: unknown[]) => void;

type FbqFn = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue?: unknown[][];
  push?: unknown;
  loaded?: boolean;
  version?: string;
};

type TrackingWindow = Window & {
  dataLayer?: unknown[];
  gtag?: GtagFn;
  fbq?: FbqFn;
  _fbq?: FbqFn;
};

// Маркира елементите, добавени от този модул, за да ги различаваме от кода,
// заложен статично в index.html.
const MARKER = 'data-cv-tracking';

export const GTM_ID_PATTERN = /^GTM-[A-Z0-9]+$/;
export const GA4_ID_PATTERN = /^G-[A-Z0-9]+$/;
export const GOOGLE_ADS_ID_PATTERN = /^AW-[0-9]+$/;
export const META_PIXEL_ID_PATTERN = /^[0-9]{8,20}$/;

let applied = false;
let managedGa4Ids: string[] = [];
let metaPixelLoaded = false;
let spaPageViewsEnabled = false;

const getWindow = (): TrackingWindow => window as TrackingWindow;

const getDataLayer = (): unknown[] => {
  const w = getWindow();
  w.dataLayer = w.dataLayer || [];
  return w.dataLayer;
};

const getGtag = (): GtagFn => {
  const w = getWindow();
  getDataLayer();
  if (!w.gtag) {
    // Точно както в официалния snippet на Google - gtag.js очаква
    // arguments обект в dataLayer, а не масив.
    w.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      w.dataLayer!.push(arguments);
    } as GtagFn;
  }
  return w.gtag;
};

/**
 * Извлича ID на таг от поставен код (целия snippet) или от чист ID.
 * Пример: целият GTM snippet -> "GTM-ABC1234"
 */
export const extractTagId = (
  value: string,
  prefix: 'GTM' | 'G' | 'AW' | 'META'
): string => {
  if (prefix === 'META') {
    const match = value.match(/fbq\(\s*['"]init['"]\s*,\s*['"]?([0-9]{8,20})/);
    if (match) return match[1];
    const digits = value.trim();
    return META_PIXEL_ID_PATTERN.test(digits) ? digits : digits.replace(/\D/g, '');
  }

  const pattern =
    prefix === 'AW' ? /AW-[0-9]+/ : prefix === 'GTM' ? /GTM-[A-Z0-9]+/ : /G-[A-Z0-9]+/;
  const match = value.toUpperCase().match(pattern);
  return match ? match[0] : value.trim().toUpperCase();
};

let preloadedIds: string[] | null = null;

/**
 * Връща ID-тата, които вече са заложени статично в index.html, за да не ги
 * добавим втори път от админ панела (двойно броене на посещения).
 *
 * Резултатът се запомня от първото извикване. Самият gtag.js добавя свои
 * скриптове след зареждане и без това те биха се разпознали като "заложени в
 * index.html" при по-късно извикване (напр. от админ панела).
 */
export const getPreloadedTagIds = (): string[] => {
  if (preloadedIds) return preloadedIds;

  const ids = new Set<string>();
  const scripts = document.querySelectorAll<HTMLScriptElement>(`script:not([${MARKER}])`);

  scripts.forEach((script) => {
    const src = script.getAttribute('src') || '';
    if (src.includes('googletagmanager.com')) {
      const idFromSrc = src.match(/[?&]id=([^&"']+)/);
      if (idFromSrc) ids.add(decodeURIComponent(idFromSrc[1]));
    }

    if (!src && script.textContent) {
      const configMatches = script.textContent.matchAll(
        /gtag\(\s*['"]config['"]\s*,\s*['"]([^'"]+)['"]/g
      );
      for (const match of configMatches) ids.add(match[1]);
    }
  });

  preloadedIds = Array.from(ids);
  return preloadedIds;
};

/**
 * Намира ID-тата на тагове в свободно поставен код. Използва се, за да
 * разпознаем snippet, който дублира вече конфигуриран таг.
 */
export const findTagIdsInCode = (code: string): string[] => {
  const ids = new Set<string>();
  const patterns = [/GTM-[A-Z0-9]+/g, /G-[A-Z0-9]+/g, /AW-[0-9]+/g];

  patterns.forEach((pattern) => {
    for (const match of code.toUpperCase().matchAll(pattern)) ids.add(match[0]);
  });

  for (const match of code.matchAll(/fbq\(\s*['"]init['"]\s*,\s*['"]?([0-9]{8,20})/g)) {
    ids.add(match[1]);
  }

  return Array.from(ids);
};

const injectGtm = (containerId: string) => {
  if (document.querySelector(`script[src*="gtm.js?id=${containerId}"]`)) return;

  getDataLayer().push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(containerId)}`;
  script.setAttribute(MARKER, 'gtm');
  document.head.appendChild(script);
};

const injectGtag = (ids: string[]) => {
  if (ids.length === 0) return;

  const gtag = getGtag();
  const alreadyLoaded = document.querySelector('script[src*="googletagmanager.com/gtag/js"]');

  if (!alreadyLoaded) {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(ids[0])}`;
    script.setAttribute(MARKER, 'gtag');
    document.head.appendChild(script);
    gtag('js', new Date());
  }

  ids.forEach((id) => gtag('config', id));
};

const injectMetaPixel = (pixelId: string) => {
  const w = getWindow();
  if (document.querySelector('script[src*="connect.facebook.net"]')) return;

  // Еквивалент на официалния snippet на Meta: докато fbevents.js се зареди,
  // извикванията се трупат в опашка и се изпълняват след това.
  const fbq = function (...args: unknown[]) {
    if (fbq.callMethod) fbq.callMethod(...args);
    else fbq.queue!.push(args);
  } as FbqFn;

  fbq.queue = [];
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  w.fbq = fbq;
  w._fbq = w._fbq || fbq;

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  script.setAttribute(MARKER, 'meta-pixel');
  document.head.appendChild(script);

  fbq('init', pixelId);
  fbq('track', 'PageView');
  metaPixelLoaded = true;
};

const recreateScript = (original: HTMLScriptElement): HTMLScriptElement => {
  const script = document.createElement('script');
  Array.from(original.attributes).forEach((attr) => script.setAttribute(attr.name, attr.value));
  script.text = original.text;
  return script;
};

/**
 * Инжектира свободно поставен код, като пропуска:
 *  - <noscript> блокове - при инжектиране от JavaScript те не са "no script",
 *    а реални елементи, които зареждат iframe и водят до двойно отчитане;
 *  - скриптове с ID на таг, който вече е зареден (напр. целия GTM snippet,
 *    поставен освен в полето за Container ID).
 */
const injectCustomCode = (
  code: string,
  target: HTMLElement,
  name: string,
  loadedIds: string[]
) => {
  if (!code.trim()) return;
  if (document.querySelector(`[${MARKER}="${name}"]`)) return;

  const template = document.createElement('template');
  template.innerHTML = code;

  Array.from(template.content.childNodes).forEach((node) => {
    if (node.nodeName === 'NOSCRIPT') {
      console.info('[tracking] Пропуснат <noscript> блок в допълнителния код.');
      return;
    }

    if (node.nodeName === 'SCRIPT') {
      const original = node as HTMLScriptElement;
      const content = `${original.getAttribute('src') || ''} ${original.text}`;
      const duplicate = loadedIds.find((id) => id && content.includes(id));

      if (duplicate) {
        console.info(
          `[tracking] Пропуснат код в допълнителния ${name}: ${duplicate} вече е зареден.`
        );
        return;
      }

      const script = recreateScript(original);
      script.setAttribute(MARKER, name);
      target.appendChild(script);
      return;
    }

    if (node instanceof HTMLElement) node.setAttribute(MARKER, name);
    target.appendChild(node);
  });
};

/**
 * Зарежда всички тагове, конфигурирани от админ панела. Изпълнява се веднъж
 * за живота на страницата - повторните извиквания се игнорират.
 */
export const applyTrackingSettings = (settings: TrackingSettings | null) => {
  if (applied || !settings || !settings.enabled) return;
  applied = true;

  const preloadedIds = getPreloadedTagIds();
  spaPageViewsEnabled = settings.trackSpaPageViews;

  const gtmId = settings.gtmContainerId.trim();
  if (gtmId && !preloadedIds.includes(gtmId)) {
    injectGtm(gtmId);
  }

  const gtagIds = [settings.ga4MeasurementId, settings.googleAdsId]
    .map((id) => id.trim())
    .filter((id) => id && !preloadedIds.includes(id));

  managedGa4Ids = gtagIds.filter((id) => GA4_ID_PATTERN.test(id));
  injectGtag(gtagIds);

  const metaPixelId = settings.metaPixelId.trim();
  if (metaPixelId) injectMetaPixel(metaPixelId);

  const loadedIds = [...preloadedIds, gtmId, ...gtagIds, metaPixelId].filter(Boolean);
  injectCustomCode(settings.customHeadCode, document.head, 'custom-head', loadedIds);
  injectCustomCode(settings.customBodyCode, document.body, 'custom-body', loadedIds);
};

/**
 * Изпраща page_view при навигация в SPA-то. Първоначалното зареждане се
 * отчита от самия GTM/gtag/пиксел, затова се извиква само при смяна на
 * страницата.
 */
export const trackPageView = (path: string) => {
  if (!applied || !spaPageViewsEnabled) return;

  const pageData = {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  };

  getDataLayer().push({ event: 'page_view', ...pageData });

  const w = getWindow();

  if (managedGa4Ids.length > 0) {
    const gtag = getGtag();
    managedGa4Ids.forEach((id) => gtag('event', 'page_view', { send_to: id, ...pageData }));
  }

  if (metaPixelLoaded && w.fbq) w.fbq('track', 'PageView');
};

/**
 * Изпраща събитие едновременно към dataLayer (за GTM/GA4) и към Meta Pixel.
 *
 * @param name       име на събитието в dataLayer (GA4 конвенция, напр. generate_lead)
 * @param metaEvent  съответното стандартно събитие на Meta (напр. Lead); при
 *                   празна стойност се изпраща само към dataLayer
 */
export const trackEvent = (
  name: string,
  metaEvent?: string,
  params: Record<string, unknown> = {}
) => {
  if (!applied) return;

  getDataLayer().push({ event: name, ...params });

  const w = getWindow();
  if (metaEvent && metaPixelLoaded && w.fbq) w.fbq('track', metaEvent, params);
};
